import Button from "@/components/common/Button";
import { API_BASE_URL } from "@/lib/axios";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";


const schema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof schema>;

const ResetPassword = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const id = searchParams.get("id");

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>();

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  const endpoint =
    `${API_BASE_URL}/api/v1/authentication/company/reset-password`;

  const onSubmit = async (_data: FormValues) => {
    setIsLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000); // 10s timeout

    try {
      if (!token || !id) {
        throw new Error(t("auth.invalidResetLink"));
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id,
          token,
          password: _data.password,
          passwordConfirm: _data.confirmPassword,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeout);
      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Reset failed");

      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err: any) {
      if (err.name === "AbortError") {
        setError(
          t("auth.requestTimeout") || "Request timed out. Please try again."
        );
      } else {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong.";
        if (msg.includes("Invalid or expired reset link")) {
          setError(t("auth.invalidResetLink"));
        } else if (msg.includes("Passwords do not match")) {
          setError(t("auth.passwordsDontMatch"));
        } else if (msg.includes("Password reset failed")) {
          setError(t("auth.passwordResetFailed"));
        } else {
          setError(msg);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-slate-800 p-8 rounded-xl shadow-lg w-full max-w-md relative"
      >
        <h1 className="text-2xl font-bold text-white mb-4">
          Create New Password
        </h1>
        <p className="mb-6 text-slate-400">Enter your new password</p>
        <div className="mb-4">
          <label className="block text-slate-300 mb-1">Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              className="w-full p-3 rounded bg-slate-700 text-white border border-slate-600"
              {...register("password", { required: true })}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-white"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-slate-300 mb-1">Confirm Password</label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="w-full p-3 rounded bg-slate-700 text-white border border-slate-600"
              {...register("confirmPassword", { required: true })}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-white"
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          className="w-full mt-2"
          disabled={isLoading || !password || !confirmPassword}
        >
          {isLoading ? "Resetting..." : "Reset Password"}
        </Button>

        {(success || error) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-slate-800/90 border border-slate-700 rounded-2xl p-8 shadow-2xl w-full max-w-md text-center relative">
              <button
                className="absolute top-3 right-3 text-slate-400 hover:text-white text-2xl"
                onClick={() => {
                  setSuccess(false);
                  setError(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
              <h2 className="text-2xl font-semibold text-white mb-2">
                {success
                  ? "Password Changed Successfully"
                  : "Password Change Failed"}
              </h2>
              <p className="text-slate-400 text-sm mb-6">
                {success
                  ? "Your password has been updated. Redirecting to login..."
                  : error}
              </p>
              <Button
                onClick={() => {
                  setSuccess(false);
                  setError(null);
                }}
                className="w-full h-12 bg-cyan-500 hover:bg-cyan-600 text-white font-medium rounded-lg transition-colors"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ResetPassword;
